import React from 'react';
import { useTranslation } from 'react-i18next';
import { useTranslationKey } from '@jwp/ott-hooks-react/src/useTranslationKey';
import useBreakpoint, { Breakpoint } from '@jwp/ott-ui-react/src/hooks/useBreakpoint';
import type { PlaylistItem } from '@jwp/ott-common/types/playlist';
import { countListValues, createLiveEventMetadata } from '@jwp/ott-common/src/utils/metadata';
import { mediaURL } from '@jwp/ott-common/src/utils/urlFormatting';
import useMovieParams from '@jwp/ott-ui-react/src/hooks/useMovieParams';
import useMovieHandlers from '@jwp/ott-ui-react/src/hooks/useMovieHandlers';
import useTrailerData from '@jwp/ott-hooks-react/src/useTrailerData';
import useAutoFocus from '@jwp/ott-ui-react/src/hooks/useAutoFocus';
import type { ScreenComponent } from '@jwp/ott-ui-react/types/screens';
import Cinema from '@jwp/ott-ui-react/src/containers/Cinema/Cinema';
import TrailerModal from '@jwp/ott-ui-react/src/containers/TrailerModal/TrailerModal';
import ShareButton from '@jwp/ott-ui-react/src/components/ShareButton/ShareButton';
import FavoriteButton from '@jwp/ott-ui-react/src/containers/FavoriteButton/FavoriteButton';
import VideoMetaData from '@jwp/ott-ui-react/src/components/VideoMetaData/VideoMetaData';
import VideoTags from '@jwp/ott-ui-react/src/components/VideoTags/VideoTags';
import EventTags from '@jwp/ott-ui-react/src/components/EventTags/EventTags';
import VideoDefinitionList from '@jwp/ott-ui-react/src/components/VideoDefintionsList/VideoDefinitionList';
import MediaHead from '@jwp/ott-ui-react/src/components/MediaHead/MediaHead';
import MediaHeroInfo from '@jwp/ott-ui-react/src/components/MediaHeroInfo/MediaHeroInfo';
import MediaHeroButtons from '@jwp/ott-ui-react/src/components/MediaHeroButtons/MediaHeroButtons';
import MediaHero from '@jwp/ott-ui-react/src/components/MediaHero/MediaHero';
import MediaTabs from '@jwp/ott-ui-react/src/components/MediaTabs/MediaTabs';
import MarkdownComponent from '@jwp/ott-ui-react/src/components/MarkdownComponent/MarkdownComponent';
import MediaTabAbout from '@jwp/ott-ui-react/src/components/MediaTabAbout/MediaTabAbout';
import MediaButtonTrailer from '@jwp/ott-ui-react/src/components/MediaButtonTrailer/MediaButtonTrailer';
import StartWatchingButton from '@jwp/ott-ui-react/src/containers/StartWatchingButton/StartWatchingButton';
import CardGrid from '@jwp/ott-ui-react/src/components/CardGrid/CardGrid';
import env from '@jwp/ott-common/src/env';
import styles from '@jwp/ott-ui-react/src/pages/ScreenRouting/mediaScreens/MediaMovie/MediaMovie.module.scss';
import useLiveSeriesData from '@jwp/ott-hooks-react/src/useLiveSeriesData';
import { isExpired as isExpiredCheck, isPlayable as isPlayableCheck } from '@jwp/ott-common/src/utils/liveEvent';
import { useSearchParams } from 'react-router';

import EpisodeList from '../../../../components/EpisodeList/EpisodeList';
import useQueryParam from '../../../../hooks/useQueryParam';
import { useAutoScroll } from '../../../../hooks/useAutoScroll';
import MediaDescription from '../../../../components/MediaDescription/MediaDescription';

const MediaLiveSeries: ScreenComponent<PlaylistItem> = ({ data, isLoading }) => {
  const { t, i18n } = useTranslation('video');
  const translationKey = useTranslationKey('title');
  const breakpoint = useBreakpoint();
  const { id, play, feedId } = useMovieParams();
  const episodeIdParam = useQueryParam('e');
  const [, setSearchParams] = useSearchParams();
  const {
    episode,
    playingItem,
    isPlayable,
    accessModel,
    isLoggedIn,
    hasSubscription,
    playlist,
    episodesPlaylist,
    areEpisodesEntitled,
    isEpisodesFetching,
    playUrl,
    movieURL,
    isFavoritesEnabled,
    isSharingEnabled,
    isEntitled,
    primaryMetadata,
    liveEventMetadata,
    isPlaylistLoading,
    videoRating,
    videoFormats,
    channel,
    watchHistory,
    getCardUrl,
  } = useLiveSeriesData(data, id, episodeIdParam, feedId);
  const { handleBack, handleComplete } = useMovieHandlers(undefined, movieURL);
  const { isTrailerPlaying, hasTrailer, trailerItem, trailerTitle, closeTrailer, playTrailer } = useTrailerData(data);

  const canonicalUrl = data ? `${env.APP_PUBLIC_URL}${mediaURL({ id: data.mediaid, title: data.title })}` : window.location.href;
  const smallScreen = breakpoint < Breakpoint.md;

  const handleEpisodeClick = (item: PlaylistItem) => {
    const canPlayEpisode = isPlayableCheck(item) && !isExpiredCheck(item, channel?.catchupHours);

    setSearchParams((prev) => {
      if (canPlayEpisode) {
        prev.set('play', '1');
      } else {
        prev.delete('play');
      }
      prev.set('e', item.mediaid);

      return prev;
    });
  };

  useAutoFocus(episode?.mediaid);
  useAutoScroll(episode?.mediaid);

  return (
    <React.Fragment>
      <MediaHead canonicalUrl={canonicalUrl} data={data} />
      <MediaHero image={data.backgroundImage}>
        <MediaHeroInfo
          title={data.title}
          description={<MediaDescription item={data} />}
          eventdataComponent={<EventTags mediaStatus={episode?.mediaStatus} attributes={liveEventMetadata} channel={channel} />}
          metadataComponent={<VideoMetaData attributes={primaryMetadata} />}
          tagsComponent={<VideoTags attributes={videoFormats} />}
          ratingsComponent={<VideoTags attributes={videoRating} />}
          definitionComponent={
            <VideoDefinitionList
              splitByComma={false}
              listProperties={[
                { label: t('video:metadata.actors', { count: countListValues(data?.actors as string) }), value: data?.actors as string },
                { label: t('video:metadata.directors', { count: countListValues(data?.directors as string) }), value: data?.directors as string },
              ]}
            />
          }
        />
        <MediaHeroButtons
          cta={
            <>
              <StartWatchingButton
                key={id} // necessary to fix autofocus on TalkBack
                item={playingItem}
                playUrl={playUrl}
                disabled={!isPlayable}
                channel={channel}
              />
              {!smallScreen && (
                <MediaButtonTrailer hasTrailer={hasTrailer} trailerItem={trailerItem} isTrailerPlaying={isTrailerPlaying} onClick={playTrailer} />
              )}
            </>
          }
        >
          {smallScreen && <MediaButtonTrailer hasTrailer={hasTrailer} trailerItem={trailerItem} isTrailerPlaying={isTrailerPlaying} onClick={playTrailer} />}
          {isFavoritesEnabled ? <FavoriteButton item={data} /> : undefined}
          {isSharingEnabled ? <ShareButton title={data.title} description={data.description} url={canonicalUrl} /> : undefined}
        </MediaHeroButtons>
      </MediaHero>
      <MediaTabs
        titles={[
          episodesPlaylist.playlist.length ? t('video:broadcasts') : '',
          playlist?.playlist.length ? (playlist?.[translationKey] as string) || playlist?.title || '' : '',
          t('video:about_tab'),
        ]}
        isLoading={isLoading || isEpisodesFetching || isPlaylistLoading}
      >
        {!!episodesPlaylist.playlist.length && (
          <EpisodeList
            hasMore={false}
            playlist={episodesPlaylist}
            isLoading={isEpisodesFetching}
            watchHistory={watchHistory}
            onEpisodeClick={handleEpisodeClick}
            loadMore={() => undefined}
            renderEpisodeMetadata={(episode) => (
              <EventTags
                mediaStatus={episode.mediaStatus}
                channel={channel}
                attributes={createLiveEventMetadata(episode.scheduledStart, episode.scheduledEnd, i18n.language)}
                showLiveTagOnly={false}
                small
              />
            )}
            areEpisodesEntitled={areEpisodesEntitled}
          />
        )}
        {playlist && (
          <CardGrid
            playlist={playlist}
            isLoading={isLoading || isPlaylistLoading}
            accessModel={accessModel}
            isLoggedIn={isLoggedIn}
            currentCardItem={data}
            currentCardLabel={t('video:current_video')}
            hasSubscription={hasSubscription}
            getUrl={getCardUrl}
          />
        )}
        <MediaTabAbout description={<MarkdownComponent className={styles.markdown} markdownString={data.description} headerOffset={1} />}>
          <div className={styles.aboutDefinitionList}>
            <VideoDefinitionList
              className={styles.definitionList}
              splitByComma={false}
              listProperties={[
                { label: t('video:metadata.directors', { count: countListValues(data?.directors as string) }), value: data?.directors as string },
                { label: t('video:metadata.cast'), value: data?.cast as string },
                { label: t('video:metadata.rating'), value: videoRating.length ? <VideoTags attributes={videoRating} /> : null },
                { label: t('video:metadata.formats'), value: videoFormats.length ? <VideoTags attributes={videoFormats} /> : null },
              ]}
            />
            <VideoDefinitionList
              className={styles.definitionList}
              splitByComma={false}
              listProperties={[
                { label: t('video:metadata.genres', { count: countListValues(data?.genre as string) }), value: data?.genre as string },
                { label: t('video:metadata.audio_languages'), value: data?.audioLanguages as string },
                { label: t('video:metadata.subtitle_languages'), value: data?.subtitleLanguages as string },
              ]}
            />
          </div>
        </MediaTabAbout>
      </MediaTabs>
      {playingItem && (
        <Cinema
          open={play && isEntitled}
          onClose={handleBack}
          item={playingItem}
          title={episode?.title || playingItem.title}
          primaryMetadata={<VideoMetaData attributes={primaryMetadata} />}
          onComplete={handleComplete}
          feedId={feedId ?? undefined}
          onNext={handleComplete}
        />
      )}
      <TrailerModal item={trailerItem} title={trailerTitle} open={isTrailerPlaying} onClose={closeTrailer} />
    </React.Fragment>
  );
};

export default MediaLiveSeries;
